import * as React from 'react';

import propTypes from './prop_types';
import useStyles from './use_styles';
import { ScrollbarInterface } from './types';
import { useTheme } from '@veneer/theme';
import highContrastMediaQuery from '../utils/high_contrast_media_query';
import mergeRefs from 'react-merge-refs';
import useIsOverflow from '../utils/use_is_overflow';

const Scrollbar = React.forwardRef<
  HTMLElement,
  ScrollbarInterface & React.HTMLAttributes<HTMLElement>
>(
  (
    {
      alwaysVisible = true,
      applyTo,
      cascadeStyle = false,
      children,
      component: Container = 'div',
      customStyle,
      roundMargin,
      sharpMargin,
      pillMargin,
      hideFocusRing = false,
      tabIndex,
      ...scrollbarProps
    },
    ref
  ) => {
    const theme = useTheme();
    const scrollbarRef = React.useRef<HTMLElement>(null);
    const isHorizontallyOverflowing = useIsOverflow({
      ref: scrollbarRef,
      isVertical: false,
    });
    const isVerticallyOverflowing = useIsOverflow({
      ref: scrollbarRef,
      isVertical: true,
    });

    React.useEffect(() => {
      if (Container === 'body')
        document.body.classList.add('body-veneer-scrollbar');
    }, [Container]);

    const style = useStyles({
      applyTo,
      cascadeStyle,
      roundMargin,
      sharpMargin,
      pillMargin,
    });

    const isHc = theme.mode === 'contrast';

    return (
      <Container
        css={[
          style.base,
          !isHc && style.customScroll,
          !hideFocusRing && !cascadeStyle && style.focusRing,
          alwaysVisible && style.alwaysVisible,
          isHc && highContrastMediaQuery(theme),
          customStyle,
        ]}
        tabIndex={
          tabIndex === null
            ? undefined
            : (tabIndex ?? cascadeStyle)
              ? -1
              : isHorizontallyOverflowing || isVerticallyOverflowing
                ? 0
                : -1
        }
        ref={mergeRefs([ref, scrollbarRef])}
        {...scrollbarProps}
      >
        {children}
      </Container>
    );
  }
);

Scrollbar.propTypes = propTypes;

Scrollbar.displayName = 'Scrollbar';

export default Scrollbar as <C extends React.ElementType>(
  props: ScrollbarInterface<C> &
    React.ComponentProps<C> &
    React.RefAttributes<React.ComponentRef<C>>
) => React.ReactElement | null;
